import React from 'react';

interface TourProgressProps {
  /** Current step (1-indexed) */
  current: number;
  /** Total number of steps */
  total: number;
  /** Display style */
  variant?: 'dots' | 'text' | 'bar';
  /** Size of dots/bar */
  size?: 'sm' | 'md' | 'lg';
  /** Custom class name */
  className?: string;
}

/**
 * Progress indicator for tours
 * Shows current step position in the tour
 * Matches Figma design: https://www.figma.com/design/iZgWr92Xk6B40x4JF1fyOW/GetSign---New?node-id=12122-80726
 */
export const TourProgress: React.FC<TourProgressProps> = ({
  current,
  total,
  variant = 'dots',
  size = 'md',
  className,
}) => {
  if (total <= 1) return null;

  const sizeStyles = {
    sm: { width: 6, height: 6 },
    md: { width: 8, height: 8 },
    lg: { width: 10, height: 10 },
  };

  const gapStyles = {
    sm: 4,
    md: 6,
    lg: 8,
  };

  if (variant === 'text') {
    return (
      <span
        className={className}
        style={{
          fontSize: 14,
          color: '#676879',
          fontFamily: "'Figtree', sans-serif",
        }}
      >
        Step {current} of {total}
      </span>
    );
  }

  if (variant === 'bar') {
    const percentage = (current / total) * 100;
    const barHeight = {
      sm: 4,
      md: 6,
      lg: 8,
    };

    return (
      <div
        className={className}
        style={{
          width: '100%',
          height: barHeight[size],
          backgroundColor: '#E6E9EF',
          borderRadius: 999,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${percentage}%`,
            height: '100%',
            backgroundColor: '#0073EA',
            borderRadius: 999,
            transition: 'width 0.3s ease-out',
          }}
        />
      </div>
    );
  }

  // Dots variant (default)
  return (
    <div
      className={className}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: gapStyles[size],
      }}
    >
      {Array.from({ length: total }, (_, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === current;

        return (
          <div
            key={index}
            style={{
              width: isActive ? sizeStyles[size].width + 2 : sizeStyles[size].width,
              height: sizeStyles[size].height,
              borderRadius: 999,
              backgroundColor: isActive ? '#0073EA' : '#C5C7D0',
              transition: 'all 0.2s ease',
            }}
          />
        );
      })}
    </div>
  );
};

export default TourProgress;
